import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { getFirstAndLastNames } from "../../utils/Functions";
import { Button } from "@material-ui/core";
import styled from "styled-components";
import { config } from "../../config";
import { getTagConfig } from "../../utils/tagConfig";

const StyledButton = styled(Button)`
    && {
        min-width: 200px;
        height: 40px;
        font-weight: 600;

        @media (max-width: 768px) {
            width: 100%;
        }
    }
`;

const BatchPrint = ({ people, fields, eventId = 1 }) => {
    const printRef = useRef();
    
    // Resolve dynamic tag configuration
    const tagConfig = React.useMemo(() => getTagConfig(eventId), [eventId]);

    // Respect fields parameter from checkboxes if supplied, otherwise fallback to tagConfig settings
    const showQr = fields?.qrcode !== undefined ? fields.qrcode : tagConfig.showQr;
    const showName = fields?.name !== undefined ? fields.name : tagConfig.showName;
    const showCompany = fields?.company !== undefined ? fields.company : tagConfig.showCompany;
    const showRole = fields?.role !== undefined ? fields.role : tagConfig.showRole;

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: "Credenciais",
        pageStyle: `
            @page {
                size: ${tagConfig.widthMm}mm ${tagConfig.heightMm}mm;
                margin: 0;
            }
            body {
                margin: 0;
                background: #fff;
                -webkit-print-color-adjust: exact;
            }
            @media print {
                .batch-tag-container {
                    border: none !important;
                    box-shadow: none !important;
                    background: white !important;
                    margin: 0 !important;
                    page-break-inside: avoid !important;
                    page-break-after: always !important;
                }
            }
        `,
    });

    return (
        <div>
            {/* Área escondida para impressão */}
            <div style={{ display: "none" }}>
                <div ref={printRef}>
                    {people.map((person) => (
                        <div
                            key={person.id}
                            className="batch-tag-container"
                            style={{
                                width: `${tagConfig.widthMm}mm`,
                                height: `${tagConfig.heightMm}mm`,
                                position: "relative",
                                boxSizing: "border-box",
                                overflow: "hidden",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            {/* Elemento: QR Code */}
                            {showQr && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: `${tagConfig.qrCodePosition?.top || 0}mm`,
                                        left: `${tagConfig.qrCodePosition?.left || 0}mm`,
                                        width: `${tagConfig.qrCodeSizeMm || 25}mm`,
                                        height: `${tagConfig.qrCodeSizeMm || 25}mm`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {person.id && (
                                        <img
                                            src={`${config.QRCODE_HOST || "https://api.qrserver.com/v1/create-qr-code"}/?size=150x150&data=${person.id}`}
                                            alt="QR Code"
                                            style={{ width: "100%", height: "100%" }}
                                        />
                                    )}
                                </div>
                            )}

                            {/* Elemento: Nome */}
                            {showName && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: `${tagConfig.namePosition?.top || 0}mm`,
                                        left: `${tagConfig.namePosition?.left || 0}mm`,
                                        fontSize: `${tagConfig.nameFontSize || 14}pt`,
                                        fontWeight: "700",
                                        fontFamily: "'Inter', 'Roboto', sans-serif",
                                        color: "#000000",
                                        whiteSpace: "nowrap",
                                        lineHeight: "1.2",
                                    }}
                                >
                                    {person.name ? getFirstAndLastNames(person.name_tag || person.name) : ""}
                                </div>
                            )}

                            {/* Elemento: Empresa */}
                            {showCompany && person.company && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: `${tagConfig.companyPosition?.top || 0}mm`,
                                        left: `${tagConfig.companyPosition?.left || 0}mm`,
                                        fontSize: `${tagConfig.companyFontSize || 12}pt`,
                                        fontWeight: "600",
                                        fontFamily: "'Inter', 'Roboto', sans-serif",
                                        color: "#37474f",
                                        whiteSpace: "nowrap",
                                        lineHeight: "1.2",
                                    }}
                                >
                                    {person.company}
                                </div>
                            )}

                            {/* Elemento: Cargo */}
                            {showRole && person.role && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: `${tagConfig.rolePosition?.top || 0}mm`,
                                        left: `${tagConfig.rolePosition?.left || 0}mm`,
                                        fontSize: `${tagConfig.roleFontSize || 10}pt`,
                                        fontStyle: "italic",
                                        fontFamily: "'Inter', 'Roboto', sans-serif",
                                        color: "#546e7a",
                                        whiteSpace: "nowrap",
                                        lineHeight: "1.2",
                                    }}
                                >
                                    {person.role}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <StyledButton variant="contained" color="primary" onClick={handlePrint} disabled={people.length === 0}>
                Imprimir {people.length > 0 ? `(${people.length})` : ""}
            </StyledButton>
        </div>
    );
};

export default BatchPrint;
